import { PageInfo } from "@/components/Pagination";
import { gql } from "@apollo/client";
import type { BankConnectionProvider } from "./bankConnectionProvider";

export const GET_PAYOUT_PROFILES_QUERY = gql`
  query GetPayoutProfiles($pgaId: String!, $payoutProfileId: String) {
    payoutProfiles(filters: { pgaId: $pgaId, payoutProfileId: $payoutProfileId }) {
      nodes {
        id
        name
        address
        address2
        city
        stateDisplay
        postalCode
        taxClassification
        llcClassification
        exemptPayeeCode
        taxFormType
        status
        defaultPayoutProfile
        businessName
        createdAt
        updatedAt
        allowedActions
        fatcaExemptionCode
        backupWithholding
        countryOfCitizenshipDisplay
        bankName
        bankAccountName
        bankAccountType
        bankAccountMaskedNumber
        bankAccountDisplayName
        bankConnectionProvider
        bankReauthorizationRequired
        stripeFcExpectedExpiryDate
        stripeFcAccountNumberStatus
        stripeFcAccountNumberIdentifierType
        workdayUserError
        workdayServerException
        workdaySupplierId
        workdaySupplierUrl
        signature
        signatureDate
        ssn {
          last4
          full
        }
        ein {
          last4
          full
        }
        countryDisplay
        mailingAddress
        mailingAddress2
        mailingCity
        mailingStateDisplay
        mailingPostalCode
        mailingCountryDisplay
        foreignTaxIdRequired
        w8ReferenceNumber
        dateOfBirth
        treatyCountryDisplay
        specialRatesArticle
        specialRatesPercent
        specialRatesIncomeType
        specialRatesConditions
        foreignTaxId {
          last4
          full
        }
        paperless1099
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export enum PayoutProfileAllowedActionEnum {
  DELETE_PAYOUT_PROFILE = "DELETE_PAYOUT_PROFILE",
  DEACTIVATE_PAYOUT_PROFILE = "DEACTIVATE_PAYOUT_PROFILE",
  MARK_INACTIVE_PAYOUT_PROFILE = "MARK_INACTIVE_PAYOUT_PROFILE"
}

export type TaxIdType = {
  last4?: string | null;
  full?: string | null;
} | null;

export interface PayoutProfile {
  id: string;
  name: string;
  address: string;
  address2?: string | null;
  city: string;
  stateDisplay: string;
  postalCode: string;
  taxClassification?: string | null;
  llcClassification?: string | null;
  exemptPayeeCode?: string | null;
  taxFormType?: string | null;
  status: string;
  defaultPayoutProfile: boolean;
  businessName?: string | null;
  createdAt: string;
  updatedAt: string;
  allowedActions?: PayoutProfileAllowedActionEnum[];
  fatcaExemptionCode?: string | null;
  backupWithholding?: boolean | null;
  countryOfCitizenshipDisplay?: string | null;
  bankName?: string | null;
  bankAccountName?: string | null;
  bankAccountType?: string | null;
  bankAccountMaskedNumber?: string | null;
  bankAccountDisplayName?: string | null;
  bankConnectionProvider?: BankConnectionProvider | null;
  bankReauthorizationRequired?: boolean;
  stripeFcExpectedExpiryDate?: string | null;
  stripeFcAccountNumberStatus?: string | null;
  stripeFcAccountNumberIdentifierType?: string | null;
  ssn?: TaxIdType;
  ein?: TaxIdType;
  signature?: string | null;
  signatureDate?: string | null;
  workdayUserError?: string | null;
  workdayServerException?: string | null;
  workdaySupplierId?: string | null;
  workdaySupplierUrl?: string | null;
  countryDisplay?: string | null;
  mailingAddress?: string | null;
  mailingAddress2?: string | null;
  mailingCity?: string | null;
  mailingStateDisplay?: string | null;
  mailingPostalCode?: string | null;
  mailingCountryDisplay?: string | null;
  foreignTaxIdRequired?: boolean | null;
  w8ReferenceNumber?: string | null;
  dateOfBirth?: string | null;
  treatyCountryDisplay?: string | null;
  specialRatesArticle?: string | null;
  specialRatesPercent?: string | null;
  specialRatesIncomeType?: string | null;
  specialRatesConditions?: string | null;
  foreignTaxId?: TaxIdType;
  paperless1099?: boolean;
}

export interface GetPayoutProfilesResponse {
  payoutProfiles: {
    nodes: PayoutProfile[];
    pageInfo: PageInfo;
    totalCount: number;
  };
}

export const SYNC_WORKDAY_SUPPLIER_MUTATION = gql`
  mutation SyncWorkdaySupplier($payoutProfileId: ID!) {
    syncWorkdaySupplier(payoutProfileId: $payoutProfileId) {
      success
      message
    }
  }
`;

export const REMOVE_PAYOUT_PROFILE_MUTATION = gql`
  mutation RemovePayoutProfile($payoutProfileId: ID!) {
    removePayoutProfile(payoutProfileId: $payoutProfileId) {
      success
      message
    }
  }
`;

export const MARK_PAYOUT_PROFILE_INACTIVE_MUTATION = gql`
  mutation MarkPayoutProfileInactive($payoutProfileId: ID!) {
    markPayoutProfileInactive(payoutProfileId: $payoutProfileId) {
      success
      message
    }
  }
`;
